import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";

//import utils
import { LocaleContext } from "../../../../routing/LangRouter";
import { formatPersianDate } from "../../../../utils/date";
import { calculateDiscountPercent, numberWithCommas } from "../../../../utils/utility";

//import constants
import BASE_URL from "../../../../constants/baseUrls";

//import general components
import BaseRating from "../../../common/base-rating/base-rating";

//import images and icons
import Icon from "../../../common/base-icon/icon";
import noImage from "../../../../assets/imgs/no-image.png";
import { Airplane, Location, Star1 } from "iconsax-react";

//import styles
import "./tour-item.scss";

function TourItem({ tourItem }) {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //transfer types
    const transferType = [
        { id: 1, name: t("air"), icon: <Airplane color="var(--dark-color)" variant="Bold" style={{ transform: "rotate(-90deg)" }} /> },
        { id: 2, name: t("ground"), icon: <Icon name="TrainIcon" /> },
        { id: 3, name: t("rail"), icon: <Icon name="BusIcon" /> },
    ];

    //check if a tour has a discount
    const hasDiscount = tourItem => {
        const adultPrice = tourItem?.prices?.[0]?.adult_price;
        const discountPrice = tourItem?.prices?.[0]?.adult_discount_price;
        return discountPrice && adultPrice - discountPrice !== adultPrice;
    };

    //handle the selection of a tour item and navigate to the tour information page
    const handleTourSelection = tourItem => {
        navigate(`/${locale}/tour-information`);
        saveTourInSessionStorage(tourItem);
    };

    //save the selected tour item in session storage
    const saveTourInSessionStorage = tourItem => {
        sessionStorage.setItem("tourInformation", JSON.stringify(tourItem));
    };

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: undefined,
        });
    };

    return (
        <Grid className="tour-item" container item xs={12} rowGap={1} onClick={() => handleTourSelection(tourItem)}>
            <Grid
                className="tour-item__tour-image"
                container
                item
                xs={12}
                direction="column"
                justifyContent="space-between"
                sx={{ backgroundImage: `url(${BASE_URL}${tourItem?.main_image || noImage})` }}
            >
                <Grid container justifyContent="space-between" alignItems="flex-start">
                    <span className="tour-item__city-name">
                        <Location color="var(--dark-color)" variant="Bold" />
                        {locale === "fa" ? tourItem?.destination_city?.name : tourItem?.destination_city?.english_name}
                    </span>
                    {hasDiscount(tourItem) && (
                        <span className="tour-item__percent">
                            {calculateDiscountPercent(tourItem?.prices?.[0]?.adult_price, tourItem?.prices?.[0]?.adult_discount_price)}
                        </span>
                    )}
                </Grid>
                <span className="tour-item__tour-name">{tourItem?.name}</span>
            </Grid>

            <Grid container item xs={12} justifyContent="space-between">
                <span className="tour-item__tour-transportation point">
                    {transferType?.[tourItem?.transfer_type - 1]?.icon}
                    {transferType?.[tourItem?.transfer_type - 1]?.name}
                </span>
                <span className="tour-item__tour-date">
                    {locale === "en" ? (
                        <>
                            {moment(tourItem.start_date).format("ddd D MMM")} - {moment(tourItem.end_date).format("ddd D MMM")}
                        </>
                    ) : (
                        <>
                            {formatDate(tourItem.start_date)} - {formatDate(tourItem.end_date)}
                        </>
                    )}
                </span>
            </Grid>

            <Grid container item xs={12} columnGap={1}>
                <Grid className="tour-item__hotel-name point" container columnGap={1} alignItems="center">
                    {tourItem.accommodations.length > 1 ? (
                        `${tourItem.accommodations.length} ${t("hotel")}`
                    ) : (
                        <>
                            {tourItem.accommodations?.[0]?.accommodation?.name}
                            <BaseRating
                                value={tourItem.accommodations?.[0]?.accommodation.star}
                                icon={Star1}
                                emptyIcon={Star1}
                                color="var(--golden-color)"
                                size="20"
                                readOnly={true}
                            />
                        </>
                    )}
                </Grid>
            </Grid>

            <Grid container item xs={12}>
                <span className="tour-item__tour-type">{tourItem.prices?.[0]?.services}</span>
            </Grid>

            <Grid className="tour-item__tour-price" container item xs={12} justifyContent="space-between" alignItems="center">
                <span>{t("priceStart")}</span>
                <span className="tour-item__detail-price">
                    {hasDiscount(tourItem) ? (
                        <>
                            <span className="tour-item__off-price">
                                {numberWithCommas(tourItem?.prices?.[0]?.adult_discount_price)} {t("currency")}
                            </span>
                            <span className="tour-item__origin-price">{numberWithCommas(tourItem?.prices?.[0]?.adult_price)}</span>
                        </>
                    ) : (
                        <span className="tour-item__off-price">
                            {numberWithCommas(tourItem?.prices?.[0]?.adult_price)} {t("currency")}
                        </span>
                    )}
                </span>
            </Grid>
        </Grid>
    );
}

export default TourItem;
